import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { Button } from '../components/ui/button';
import { PartnerForm } from '../components/PartnerForm';
import { partnerApi } from '../lib/api/partnerApi';

// Layout component
const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background">
    <header className="border-b">
      <div className="container mx-auto px-4 py-4">
        <h1 className="text-2xl font-bold">ProPay Simulator Admin</h1>
      </div>
    </header>
    <main className="container mx-auto px-4 py-8">
      {children}
    </main>
  </div>
);

const EditPartnerPage: React.FC = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();

  // Query to fetch partner details
  const {
    data: partnerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () => partnerApi.getPartner(partnerId!),
    enabled: !!partnerId,
  });

  const handleSuccess = () => {
    navigate('/partners');
  };

  const handleCancel = () => {
    navigate('/partners');
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">Loading partner details...</div>
        </div>
      </Layout>
    );
  }

  if (error || !partnerData?.partner) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center p-8 space-y-4">
          <div className="text-destructive">Failed to load partner details</div>
          <Button onClick={handleCancel} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Partners
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Edit Partner</h2>
          <Button onClick={handleCancel} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Partners
          </Button>
        </div>
        <PartnerForm
          partner={partnerData.partner}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </div>
    </Layout>
  );
};

export default EditPartnerPage;
